import { Calendar, Clock, MapPin, DollarSign, ExternalLink, ChevronRight } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import type { AppReservation } from '@/services/reservationService'
import { formatCurrency, formatDateForUI, formatTimeForUI, parseAPIDate } from '@meiuca/web-foundation'
import { useComponentImpression } from '@meiuca/analytics'
import { COMPONENT_TYPES } from '@/constants/analyticsEvents'

interface ReservationCardProps {
  reservation: AppReservation
  onClick?: () => void
  position?: number
}

interface Badge {
  text: string
  color: string
}

// Defensive label maps keyed by the raw backend enum values, with a fallback
// so unexpected statuses never crash the card.
const STATUS_BADGES: Record<string, Badge> = {
  confirmed: { text: 'Confirmada', color: 'bg-feedback-success text-white' },
  canceled: { text: 'Cancelada', color: 'bg-feedback-error text-white' },
  pending: { text: 'Pendente', color: 'bg-feedback-warning text-white' },
  completed: { text: 'Concluída', color: 'bg-text-tertiary text-white' }
}

const PAYMENT_BADGES: Record<string, Badge> = {
  unpaid: { text: 'Não pago', color: 'bg-feedback-warning/20 text-feedback-warning' },
  processing: { text: 'Processando', color: 'bg-feedback-warning/20 text-feedback-warning' },
  paid: { text: 'Pago', color: 'bg-feedback-success/20 text-feedback-success' },
  failed: { text: 'Falhou', color: 'bg-feedback-error/20 text-feedback-error' },
  refundProcessing: { text: 'Estorno em processamento', color: 'bg-feedback-warning/20 text-feedback-warning' },
  refunded: { text: 'Reembolsado', color: 'bg-text-tertiary/20 text-text-tertiary' },
  chargeback: { text: 'Contestado', color: 'bg-feedback-error/20 text-feedback-error' }
}

function BadgePill({ badge }: { badge: Badge }) {
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>{badge.text}</span>
  )
}

export function ReservationCard({ reservation, onClick, position }: ReservationCardProps) {
  const impressionRef = useComponentImpression({
    componentType: COMPONENT_TYPES.RESERVATION_CARD,
    componentKey: `reservation_${reservation.id}`,
    position,
  })
  const { venue, date, status, paymentStatus, priceCents } = reservation

  const formattedPrice = formatCurrency(priceCents)
  const reservationDate = parseAPIDate(date)

  const statusBadge = STATUS_BADGES[status] ?? STATUS_BADGES.pending
  const paymentBadge = PAYMENT_BADGES[paymentStatus]

  // Generate Google Maps link
  const getGoogleMapsLink = () => {
    if (venue.latitude && venue.longitude) {
      return `https://www.google.com/maps/search/?api=1&query=${venue.latitude},${venue.longitude}`
    } else if (venue.address) {
      const address = `${venue.address}, ${venue.city || ''} - ${venue.state || ''}`
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    }
    return null
  }

  const mapsLink = getGoogleMapsLink()
  const interactive = Boolean(onClick)

  return (
    <div
      ref={impressionRef}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-lg bg-background-white border border-border-light p-4 transition-shadow hover:shadow-md ${
        interactive ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/40' : ''
      }`}
    >
      {/* Header: Venue Name + Status */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <Body className="text-base font-semibold text-text-primary mb-1">{venue.name}</Body>
          {venue.address && (
            <Caption className="text-text-secondary flex items-start gap-1">
              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">
                {venue.address}
                {venue.city && `, ${venue.city}`}
                {venue.state && ` - ${venue.state}`}
              </span>
            </Caption>
          )}
        </div>
        <div className="flex flex-col gap-1 items-end ml-2">
          <BadgePill badge={statusBadge} />
          {paymentBadge && <BadgePill badge={paymentBadge} />}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-text-secondary mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <Body className="text-sm">{formatDateForUI(reservationDate, 'long')}</Body>
        </div>

        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <Body className="text-sm">{formatTimeForUI(reservationDate)}</Body>
        </div>
      </div>

      {/* Price and Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border-light">
        <div className="flex items-center gap-2 text-brand-primary">
          <DollarSign className="w-5 h-5" />
          <Body className="text-lg font-semibold text-brand-primary m-0">{formattedPrice}</Body>
        </div>

        <div className="flex items-center gap-1">
          {mapsLink && (
            <a
              href={mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 px-3 py-2 text-sm text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors"
            >
              <MapPin className="w-4 h-4" />
              <span>Ver no mapa</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {interactive && <ChevronRight className="w-5 h-5 text-text-tertiary" />}
        </div>
      </div>
    </div>
  )
}
